import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Eye, EyeOff, Loader2, ArrowRight, PhoneCall, CreditCard, Wallet, ArrowUpRight, ArrowDownRight, AlertTriangle, Copy, LogIn, Shield, Sun, Moon, User as UserIcon, Radio, ExternalLink, Calendar, Share2, TrendingUp, TrendingDown, Sparkles, CalendarOff, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import WIBClock from "../components/WIBClock";
import { publicAPI } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const formatIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);

const BULAN_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const formatTanggal = (t) => {
  if (!t) return "-";
  const s = String(t);
  // ISO YYYY-MM-DD
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${parseInt(m[3])} ${BULAN_ID[parseInt(m[2]) - 1]} ${m[1]}`;
  // DD/MM/YYYY
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${parseInt(m[1])} ${BULAN_ID[parseInt(m[2]) - 1]} ${m[3]}`;
  return s;
};

const LoginPage = () => {
  const { login, theme } = useApp();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const fetchSummary = () => publicAPI.summary().then(setSummary).catch(() => {});
    fetchSummary();
    // Auto-refresh every 20s for realtime feel; also on window focus
    const iv = setInterval(fetchSummary, 20000);
    const onFocus = () => fetchSummary();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) { toast.success(`Selamat datang, ${res.user.name}!`); nav("/"); }
    else toast.error(res.error);
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} disalin`);
  };

  // Prioritaskan settings LIVE dari /public/summary (auto-refresh 20s) daripada theme cache
  const s = summary?.settings || {};
  const heroImage = s.heroImage || theme.heroImage || "https://customer-assets-lqy194kg.emergentagent.net/job_sapawarga-enhanced/artifacts/5iv2s4na_Beranda%20Aplikasi%20SapaWargaDMR%231.png";
  const liveLogo = s.logo || theme.logo;
  const liveAppName = s.appName || theme.appName;
  const liveTagline = s.appTagline || theme.appTagline;
  const liveWelcome = s.welcomeText || theme.welcomeText;
  const posSatpam = s.posSatpamPhone || theme.posSatpamPhone || "-";
  const bankName = s.bankName || theme.bankName || "Bank Syariah Indonesia (BSI)";
  const bankAccount = s.bankAccount || theme.bankAccount || "7090123456";
  const bankHolder = s.bankHolder || theme.bankHolder || "Warga RT DMR";
  const footerText = s.footerText || theme.footerText || "© WARGA RT DM RESIDENCE";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Nav */}
      <div className="absolute top-0 left-0 right-0 z-30">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={liveLogo} alt="logo" className="h-14 w-14 rounded-full object-cover shadow-lg ring-2 ring-white/40" />
            <div className="leading-tight">
              <div className="font-extrabold text-white text-lg drop-shadow" style={{ fontFamily: "Poppins, sans-serif" }}>{liveAppName || "WARGA RT DMR"}</div>
              <div className="text-[10px] text-amber-300 font-bold tracking-widest">{liveTagline}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <WIBClock compact className="bg-white/90 backdrop-blur px-3 py-2 rounded-full hidden md:flex" />
            <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
              <DialogTrigger asChild>
                <Button className="bg-white text-emerald-800 hover:bg-emerald-50 font-semibold shadow-lg" data-testid="landing-login-btn"><LogIn className="h-4 w-4 mr-2" />Masuk / Login</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="text-xl">Masuk ke Akun Anda</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                  <div><Label htmlFor="email">Email</Label><Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nama@dmr.id" required className="h-11" /></div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <div className="relative"><Input id="password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="h-11 pr-10" />
                      <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-11 brand-btn text-white font-semibold">{loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Memproses...</> : "Masuk"}</Button>
                  <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                    <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-slate-500">atau</span></div>
                  </div>
                  {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                  <button
                    type="button"
                    onClick={() => {
                      const redirectUrl = window.location.origin + "/";
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    className="w-full h-11 flex items-center justify-center gap-3 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-semibold transition"
                    data-testid="google-login-btn"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09A6.98 6.98 0 015.47 12c0-.73.13-1.43.36-2.09V7.07H2.18A11 11 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
                    </svg>
                    Masuk dengan Google
                  </button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* RUNNING TEXT / MARQUEE */}
      {summary && <RunningTextBar summary={summary} formatIDR={formatIDR} />}

      {/* HERO with image */}
      <div className="relative min-h-[600px] lg:min-h-[720px] overflow-hidden">
        <img src={heroImage} alt="DM Residence" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/60 to-slate-900/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 pt-32 pb-16 text-white">
          <Badge className="bg-emerald-500/30 border border-emerald-300/40 text-emerald-100 hover:bg-emerald-500/30 mb-5 backdrop-blur">✨ SAPA WARGA DMR</Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight max-w-3xl" style={{ fontFamily: "Poppins, sans-serif" }}>
            Kelola Administrasi<br />
            <span className="text-amber-300">Warga DM Residence</span><br />
            lebih <span className="text-emerald-300">mudah & transparan</span>.
          </h1>
          <p className="mt-3 text-amber-200 font-semibold text-sm md:text-base tracking-wide">Platform Digital Sapa Warga DM Residence Garut</p>
          <p className="mt-2 text-slate-200/90 max-w-xl leading-relaxed">{liveWelcome}</p>
          <p className="mt-3 text-xs text-slate-300/80 italic">Founder & Development by. Indra Arica, SE, MM</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => setLoginOpen(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-12 px-6 shadow-xl"><LogIn className="h-4 w-4 mr-2" />Masuk ke Akun</Button>
            <a href="#info" className="inline-flex items-center gap-2 rounded-md h-12 px-6 border border-white/40 text-white font-semibold hover:bg-white/10 transition">Lihat Info Warga <ArrowRight className="h-4 w-4" /></a>
            <button onClick={() => nav("/bagikan-undangan")} className="inline-flex items-center gap-2 rounded-md h-12 px-6 bg-amber-400 hover:bg-amber-500 text-amber-950 font-bold shadow-xl transition" data-testid="landing-share-btn"><Share2 className="h-4 w-4" />Bagikan Undangan</button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div id="info" className="max-w-7xl mx-auto px-4 lg:px-8 py-10 -mt-20 relative z-20 space-y-6">

        {/* Rekening Bank - MOST IMPORTANT */}
        <Card className="border-2 border-emerald-500 shadow-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white overflow-hidden">
          <CardContent className="p-6 md:p-8 relative">
            <div className="absolute -right-10 -top-10 h-56 w-56 rounded-full bg-white/10" />
            <div className="absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-amber-400/20" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="h-16 w-16 rounded-2xl bg-white/95 grid place-items-center shrink-0"><CreditCard className="h-8 w-8 text-emerald-700" /></div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-emerald-100 font-semibold">REKENING PEMBAYARAN K3/IPL & TRANSAKSI</div>
                <div className="text-lg md:text-xl font-bold mt-1">{bankName}</div>
                <div className="flex items-center gap-3 mt-2">
                  <div className="text-2xl md:text-4xl font-black tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{bankAccount}</div>
                  <button onClick={() => copy(bankAccount, "Nomor rekening")} className="h-9 w-9 grid place-items-center rounded-lg bg-white/20 hover:bg-white/30 transition" title="Salin nomor rekening"><Copy className="h-4 w-4" /></button>
                </div>
                <div className="text-sm text-emerald-100 mt-1">a.n. <b className="text-white">{bankHolder}</b></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stat cards: Kas Summary */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="border-emerald-100 card-hover"><CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><div className="h-10 w-10 rounded-lg bg-emerald-100 grid place-items-center"><ArrowUpRight className="h-5 w-5 text-emerald-700" /></div><div className="text-xs uppercase font-bold text-slate-500">TOTAL PEMASUKAN</div></div>
            <div className="text-2xl md:text-3xl font-extrabold text-emerald-700 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{formatIDR(summary?.kas?.pemasukan)}</div>
          </CardContent></Card>
          <Card className="border-red-100 card-hover"><CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><div className="h-10 w-10 rounded-lg bg-red-100 grid place-items-center"><ArrowDownRight className="h-5 w-5 text-red-700" /></div><div className="text-xs uppercase font-bold text-slate-500">TOTAL PENGELUARAN</div></div>
            <div className="text-2xl md:text-3xl font-extrabold text-red-700 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{formatIDR(summary?.kas?.pengeluaran)}</div>
          </CardContent></Card>
          <Card className="border-2 border-amber-300 card-hover bg-amber-50/30"><CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><div className="h-10 w-10 rounded-lg brand-gradient grid place-items-center"><Wallet className="h-5 w-5 text-white" /></div><div className="text-xs uppercase font-bold text-slate-500">SALDO AKHIR KAS RT</div></div>
            <div className="text-2xl md:text-3xl font-black brand-text tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{formatIDR(summary?.kas?.saldoAkhir)}</div>
          </CardContent></Card>
        </div>

        {/* KAS BULAN INI + RIWAYAT TRANSAKSI */}
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
            <Card className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-500 to-emerald-700 text-white overflow-hidden card-hover">
              <CardContent className="p-5 relative">
                <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/10" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-9 w-9 rounded-lg bg-white/20 grid place-items-center"><TrendingUp className="h-5 w-5 text-white" /></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-100">Pemasukan Bulan Ini</div>
                      <div className="text-[11px] font-semibold text-amber-200">{summary?.kas_bulan_ini?.bulan || summary?.bulan_now}</div>
                    </div>
                  </div>
                  <div className="text-2xl md:text-3xl font-black tabular-nums mt-2" style={{ fontFamily: "Poppins, sans-serif" }} data-testid="kas-bulan-pemasukan">{formatIDR(summary?.kas_bulan_ini?.pemasukan)}</div>
                  <div className="text-[11px] text-emerald-100 mt-1">Iuran K3/IPL + pemasukan lainnya</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-rose-300 bg-gradient-to-br from-rose-500 to-red-700 text-white overflow-hidden card-hover">
              <CardContent className="p-5 relative">
                <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/10" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-9 w-9 rounded-lg bg-white/20 grid place-items-center"><TrendingDown className="h-5 w-5 text-white" /></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-rose-100">Pengeluaran Bulan Ini</div>
                      <div className="text-[11px] font-semibold text-amber-200">{summary?.kas_bulan_ini?.bulan || summary?.bulan_now}</div>
                    </div>
                  </div>
                  <div className="text-2xl md:text-3xl font-black tabular-nums mt-2" style={{ fontFamily: "Poppins, sans-serif" }} data-testid="kas-bulan-pengeluaran">{formatIDR(summary?.kas_bulan_ini?.pengeluaran)}</div>
                  <div className="text-[11px] text-rose-100 mt-1">Operasional & belanja RT</div>
                </div>
              </CardContent>
            </Card>
          </div>
          <Card className="lg:col-span-3 border-slate-200 overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-lg bg-slate-100 grid place-items-center"><Calendar className="h-5 w-5 text-slate-700" /></div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-base" style={{ fontFamily: "Poppins, sans-serif" }}>Riwayat Transaksi Terbaru</h3>
                    <p className="text-[11px] text-slate-500">Update realtime dari Kas RT & Iuran Warga</p>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-800"><Radio className="h-3 w-3 mr-1 animate-pulse" />Live</Badge>
              </div>
              {summary?.kas_recent?.length ? (
                <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto scrollbar-thin" data-testid="kas-recent-list">
                  {summary.kas_recent.map((tx, i) => (
                    <div key={i} className="flex items-center gap-3 py-2.5">
                      <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${tx.jenis === "masuk" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {tx.jenis === "masuk" ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-800 truncate">{tx.uraian}</div>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5 flex-wrap">
                          <span className="font-mono">{formatTanggal(tx.tanggal)}</span>
                          {tx.kategori && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">{tx.kategori}</span>}
                        </div>
                      </div>
                      <div className={`text-sm md:text-base font-black tabular-nums whitespace-nowrap ${tx.jenis === "masuk" ? "text-emerald-700" : "text-rose-700"}`} style={{ fontFamily: "Poppins, sans-serif" }}>
                        {tx.jenis === "masuk" ? "+" : "−"} {formatIDR(tx.jumlah)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400 text-sm italic">Belum ada transaksi tercatat</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Belum Bayar & Kontak Satpam */}
        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-rose-50 overflow-hidden shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-4">
                {/* Apologetic illustration (inline SVG) */}
                <div className="hidden sm:block shrink-0" aria-hidden="true">
                  <svg viewBox="0 0 120 120" className="h-28 w-28">
                    <defs>
                      <linearGradient id="apoGrad" x1="0" x2="1" y1="0" y2="1">
                        <stop offset="0%" stopColor="#fef3c7" />
                        <stop offset="100%" stopColor="#fecaca" />
                      </linearGradient>
                    </defs>
                    <circle cx="60" cy="60" r="55" fill="url(#apoGrad)" />
                    <circle cx="60" cy="42" r="12" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5" />
                    <path d="M60 54 Q45 62 42 82 L52 85 Q56 72 60 68 Q64 72 68 85 L78 82 Q75 62 60 54 Z" fill="#f59e0b" />
                    <ellipse cx="55" cy="41" rx="1.2" ry="1.8" fill="#78350f" />
                    <ellipse cx="65" cy="41" rx="1.2" ry="1.8" fill="#78350f" />
                    <path d="M55 47 Q60 50 65 47" fill="none" stroke="#78350f" strokeWidth="1.4" strokeLinecap="round" />
                    <path d="M52 74 L60 66 L68 74 Z" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.2" />
                    <path d="M75 34 Q72 40 75 42 Q78 40 75 34" fill="#60a5fa" opacity="0.8" />
                    <text x="60" y="112" textAnchor="middle" fontSize="9" fill="#92400e" fontWeight="700" fontFamily="sans-serif">MOHON MAAF</text>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <h3 className="font-extrabold text-amber-900 text-lg md:text-xl" style={{ fontFamily: "Poppins, sans-serif" }}>Blok Belum Bayar K3/IPL</h3>
                    <Badge className="bg-amber-500 text-white hover:bg-amber-500 text-xs" data-testid="landing-belum-count">{summary?.belum_bayar_count || 0} / {summary?.total_blok || 78} Blok</Badge>
                  </div>
                  <div className="text-sm md:text-base font-bold text-amber-800 mt-1">{summary?.bulan_now}</div>
                  <p className="text-xs md:text-sm text-amber-800 italic mt-2 leading-relaxed">
                    "Mohon maaf 🙏 diinformasikan blok berikut belum melunasi iuran bulan ini. Bila sudah bayar mohon abaikan pesan ini."
                  </p>
                </div>
              </div>
              {summary?.belum_bayar?.length ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-2 md:gap-3 max-h-[420px] overflow-y-auto scrollbar-thin pr-1" data-testid="belum-bayar-grid">
                  {summary.belum_bayar.map((b) => (
                    <div
                      key={b}
                      className="group relative aspect-square rounded-xl bg-white border-2 border-rose-200 shadow-sm hover:shadow-lg hover:border-rose-400 hover:-translate-y-0.5 transition-all p-2 flex flex-col items-center justify-center"
                      data-testid={`belum-bayar-blok-${b}`}
                    >
                      <div className="absolute top-1.5 right-1.5">
                        <div className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
                      </div>
                      <div className="text-[9px] uppercase tracking-wider font-bold text-rose-500/70 mb-0.5">Blok</div>
                      <div className="text-lg md:text-xl lg:text-2xl font-black text-rose-700 tabular-nums leading-none" style={{ fontFamily: "Poppins, sans-serif" }}>{b}</div>
                      <div className="mt-1 text-[9px] font-semibold text-rose-500 flex items-center gap-0.5"><AlertTriangle className="h-2.5 w-2.5" />Belum</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl bg-emerald-50 border-2 border-emerald-200 p-6 text-center">
                  <div className="text-4xl mb-2">🎉</div>
                  <div className="text-base font-bold text-emerald-800">Alhamdulillah, semua blok sudah bayar bulan ini!</div>
                  <div className="text-xs text-emerald-600 mt-1">Terima kasih atas kepatuhan seluruh warga DM Residence</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-100 bg-gradient-to-br from-blue-50 to-white"><CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4"><PhoneCall className="h-5 w-5 text-blue-600" /><h3 className="font-bold text-slate-800" style={{ fontFamily: "Poppins, sans-serif" }}>Kontak Pos Satpam</h3></div>
            <a href={`tel:${posSatpam.replace(/[^0-9]/g, "")}`} className="block group">
              <div className="rounded-xl p-4 bg-white border border-blue-100 group-hover:shadow-lg transition">
                <div className="text-xs text-slate-500 font-semibold">HUBUNGI 24 JAM</div>
                <div className="text-xl md:text-2xl font-black text-blue-700 font-mono mt-1">{posSatpam}</div>
                <div className="mt-3 flex items-center gap-2 text-xs text-blue-700 font-semibold"><PhoneCall className="h-3.5 w-3.5" /> Tap untuk telepon</div>
              </div>
            </a>
            <div className="text-xs text-slate-500 mt-3 leading-relaxed">Untuk laporan keamanan, gangguan lingkungan, atau situasi darurat di area komplek DM Residence.</div>
          </CardContent></Card>
        </div>

        {/* SATPAM SEDANG BERTUGAS */}
        {summary?.on_duty && (
          <Card className="border-2 border-emerald-200 bg-gradient-to-br from-slate-900 to-emerald-900 text-white overflow-hidden">
            <CardContent className="p-6 relative">
              <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-emerald-500/10" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-white/15 grid place-items-center">
                    {summary.on_duty.shift === "pagi" ? <Sun className="h-5 w-5 text-amber-300" /> : <Moon className="h-5 w-5 text-indigo-300" />}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg" style={{ fontFamily: "Poppins, sans-serif" }}>Satpam Sedang Bertugas</h3>
                    <div className="text-xs text-emerald-100/80">Shift <span className="uppercase font-bold text-amber-300">{summary.on_duty.shift}</span> • {summary.on_duty.jam} WIB • {summary.on_duty.group_name || "-"}</div>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {summary.on_duty.personel?.length > 0 ? summary.on_duty.personel.map((p, i) => (
                    <div key={p.nama || `personel-${i}`} className="rounded-xl bg-white/10 backdrop-blur border border-white/10 p-4 flex items-center gap-3">
                      {p.foto ? <img src={p.foto} alt={p.nama} className="h-14 w-14 rounded-full object-cover ring-2 ring-emerald-300" /> : <div className="h-14 w-14 rounded-full bg-white/20 grid place-items-center"><UserIcon className="h-7 w-7 text-white/80" /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{p.nama}</div>
                        {p.kontak && <a href={`tel:${p.kontak.replace(/[^0-9]/g, "")}`} className="flex items-center gap-1 text-xs text-emerald-200 hover:text-emerald-100 mt-1 font-mono"><PhoneCall className="h-3 w-3" />{p.kontak}</a>}
                      </div>
                    </div>
                  )) : <div className="col-span-full text-center py-4 text-emerald-100/60 text-sm italic">Belum ada personel tercatat untuk shift ini</div>}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* TUKANG TAMAN SEDANG BERTUGAS HARI INI */}
        {summary?.tukang_taman_today?.length > 0 && (
          <Card className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-lime-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-10 w-10 rounded-lg bg-emerald-500 text-white grid place-items-center text-xl">🌿</div>
                <div>
                  <h3 className="font-bold text-slate-800" style={{ fontFamily: "Poppins, sans-serif" }}>Tukang Taman Sedang Bertugas Hari Ini</h3>
                  <p className="text-xs text-slate-500">Lokasi & progress pembersihan lingkungan realtime</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3" data-testid="tukang-taman-today-grid">
                {summary.tukang_taman_today.map((t, i) => (
                  <div key={i} className={`rounded-xl border-2 p-4 flex gap-4 shadow-sm ${t.is_lembur ? "bg-rose-50 border-rose-300" : "bg-white border-emerald-200"}`}>
                    {t.foto_profil ? <img src={t.foto_profil} alt={t.nama} className="h-16 w-16 rounded-full object-cover ring-2 ring-emerald-300 shrink-0" /> : <div className="h-16 w-16 rounded-full bg-emerald-100 grid place-items-center shrink-0"><UserIcon className="h-8 w-8 text-emerald-500" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-bold text-slate-800 truncate">{t.nama}</div>
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">Hari ke-{t.hari_ke}</Badge>
                        {t.is_lembur && <Badge className="bg-rose-500 text-white hover:bg-rose-500 text-[10px]">LEMBUR</Badge>}
                        {t.sudah_absen && <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-[10px]">✓ Absen</Badge>}
                      </div>
                      <div className="text-sm font-bold text-emerald-800 mt-1">📍 {t.area}</div>
                      <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{t.tugas}</div>
                      <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span>🕐 {t.jam}{t.jam_absen ? ` • Absen ${t.jam_absen}` : ""}</span>
                        {t.kontak && <a href={`tel:${t.kontak.replace(/[^0-9]/g, "")}`} className="text-emerald-700 hover:underline font-mono">📞 {t.kontak}</a>}
                      </div>
                      {t.foto_bertugas?.length > 0 && (
                        <div className="flex gap-1 mt-2 overflow-x-auto">
                          {t.foto_bertugas.slice(0, 4).map((f, j) => <img key={j} src={f} alt="" className="h-14 w-14 rounded object-cover shrink-0 border-2 border-white shadow" />)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* TUKANG TAMAN LIBUR HARI INI */}
        {summary?.tukang_taman_libur_today?.length > 0 && (
          <Card className="border-2 border-slate-300 bg-slate-50" data-testid="tukang-taman-libur-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-10 w-10 rounded-lg bg-slate-400 text-white grid place-items-center"><CalendarOff className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-bold text-slate-700" style={{ fontFamily: "Poppins, sans-serif" }}>Tukang Taman Libur / Tidak Masuk Hari Ini</h3>
                  <p className="text-xs text-slate-500">Jadwal kerja otomatis bergeser ke hari berikutnya</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3" data-testid="tukang-taman-libur-grid">
                {summary.tukang_taman_libur_today.map((t, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3">
                    {t.foto_profil ? <img src={t.foto_profil} alt={t.nama} className="h-12 w-12 rounded-full object-cover ring-2 ring-slate-300 grayscale shrink-0" /> : <div className="h-12 w-12 rounded-full bg-slate-100 grid place-items-center shrink-0"><UserIcon className="h-6 w-6 text-slate-400" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-700 truncate">{t.nama}</div>
                      <div className="text-xs text-slate-500">Area terjadwal: {t.area || "-"}</div>
                      {t.catatan && <div className="text-[11px] text-amber-700 italic mt-0.5">📝 {t.catatan}</div>}
                    </div>
                    <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200 text-[10px]">LIBUR</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* SPONSOR STRIP */}
        <Card className="border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h3 className="font-bold text-slate-800" style={{ fontFamily: "Poppins, sans-serif" }}>Terima Kasih kepada Sponsor Kami</h3>
                <p className="text-xs text-slate-500">Sponsor yang mendukung program-program Warga DM Residence</p>
              </div>
              {summary?.sponsors?.length > 0 && <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">{summary.sponsors.length} sponsor aktif</Badge>}
            </div>
            {summary?.sponsors?.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="sponsor-grid">
                {summary.sponsors.map((sp) => {
                  const inner = <>
                    <img src={sp.logo} alt={sp.nama} className="max-h-16 max-w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <div className="mt-2 text-[11px] font-semibold text-slate-700 text-center truncate w-full">{sp.nama}</div>
                  </>;
                  const cls = "flex flex-col items-center justify-center h-28 rounded-xl border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md transition p-3";
                  return sp.url
                    ? <a key={sp.nama} href={sp.url} target="_blank" rel="noreferrer" className={cls}>{inner}</a>
                    : <div key={sp.nama} className={cls}>{inner}</div>;
                })}
              </div>
            ) : (
              <div className="rounded-xl border-2 border-dashed border-slate-200 py-10 text-center text-slate-400 text-sm">
                <ExternalLink className="h-6 w-6 mx-auto mb-2 opacity-50" />
                Belum ada sponsor aktif. Space ini dapat diisi oleh Admin di menu Pengaturan.
              </div>
            )}
          </CardContent>
        </Card>

        {/* LAPORAN KEGIATAN HARI INI */}
        {summary?.laporan_kegiatan_today?.length > 0 && (
          <Card className="border-slate-100" data-testid="laporan-kegiatan-section">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center"><ClipboardList className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-bold text-slate-800" style={{ fontFamily: "Poppins, sans-serif" }}>Laporan Kegiatan Hari Ini</h3>
                  <p className="text-xs text-slate-500">Pekerjaan Satpam, Tukang Taman & Petugas Sampah yang dilaporkan hari ini</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3" data-testid="laporan-kegiatan-grid">
                {summary.laporan_kegiatan_today.map((l, i) => (
                  <div key={l.id || i} className="rounded-xl border bg-white p-4 flex flex-col gap-2" data-testid={`laporan-kegiatan-card-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`text-[10px] ${l.kategori === "satpam" ? "bg-blue-100 text-blue-800 hover:bg-blue-100" : l.kategori === "tukang_taman" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}`}>
                        {l.kategori === "satpam" ? "Satpam" : l.kategori === "tukang_taman" ? "Tukang Taman" : "Petugas Sampah"}
                      </Badge>
                      {l.jam && <span className="text-[11px] text-slate-500 font-mono">{l.jam} WIB</span>}
                    </div>
                    <div className="font-semibold text-sm text-slate-800">{l.petugas_nama}</div>
                    <div className="text-xs text-slate-600 whitespace-pre-line">{l.deskripsi}</div>
                    {l.foto?.length > 0 && (
                      <div className="flex gap-1.5 mt-1 overflow-x-auto">
                        {l.foto.slice(0, 4).map((f, j) => <img key={j} src={f} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0 border shadow-sm" />)}
                        {l.foto.length > 4 && <div className="h-16 w-16 rounded-lg bg-slate-100 grid place-items-center text-xs font-bold text-slate-500 shrink-0">+{l.foto.length - 4}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* BAGIKAN UNDANGAN CTA */}
        <Card className="border-2 border-amber-400 bg-gradient-to-br from-amber-400 via-amber-500 to-emerald-600 text-white overflow-hidden shadow-2xl card-hover">
          <CardContent className="p-6 md:p-8 relative">
            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10" />
            <div className="absolute -left-10 -bottom-10 h-52 w-52 rounded-full bg-white/5" />
            <div className="relative flex flex-col md:flex-row items-center gap-6">
              <div className="h-20 w-20 md:h-24 md:w-24 rounded-3xl bg-white/95 grid place-items-center shrink-0 shadow-xl rotate-3">
                <Share2 className="h-10 w-10 md:h-12 md:w-12 text-emerald-700" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <Badge className="bg-white/20 border border-white/40 text-white hover:bg-white/20 mb-2"><Sparkles className="h-3 w-3 mr-1" />Ajak Tetangga</Badge>
                <h3 className="text-2xl md:text-3xl font-black leading-tight" style={{ fontFamily: "Poppins, sans-serif" }}>Bagikan Undangan Warga DM Residence</h3>
                <p className="mt-2 text-sm md:text-base text-white/90 max-w-xl">Undang tetangga bergabung lewat WhatsApp, Telegram, atau QR code sekali tap. Cepat, mudah & elegan.</p>
              </div>
              <button onClick={() => nav("/bagikan-undangan")} className="shrink-0 h-14 px-6 md:px-8 rounded-2xl bg-white text-emerald-800 font-black text-base hover:bg-emerald-50 shadow-xl hover:scale-105 transition flex items-center gap-2" data-testid="landing-share-cta-btn">
                <Share2 className="h-5 w-5" />Bagikan Sekarang <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center text-xs md:text-sm">
          {footerText}
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;

// Running text bar: shows Kas, Saldo, Belum bayar, Kegiatan mendatang
const RunningTextBar = ({ summary, formatIDR }) => {
  const items = [];
  const k = summary?.kas || {};
  items.push({ icon: "💰", text: `Pemasukan Kas: ${formatIDR(k.pemasukan)}` });
  items.push({ icon: "💸", text: `Pengeluaran Kas: ${formatIDR(k.pengeluaran)}` });
  items.push({ icon: "🏦", text: `Saldo Akhir Kas RT: ${formatIDR(k.saldoAkhir)}` });
  if (summary?.belum_bayar_count > 0) {
    const list = (summary.belum_bayar || []).slice(0, 12).join(", ");
    const more = summary.belum_bayar_count > 12 ? ` +${summary.belum_bayar_count - 12} lagi` : "";
    items.push({ icon: "⚠️", text: `Belum Bayar ${summary.bulan_now}: ${list}${more}` });
  } else {
    items.push({ icon: "🎉", text: `Alhamdulillah semua blok sudah bayar ${summary?.bulan_now}` });
  }
  (summary?.kegiatan || []).forEach((kg) => {
    items.push({ icon: "📅", text: `Agenda ${kg.tanggal} ${kg.jam || ""} — ${kg.nama} @ ${kg.lokasi || "-"}` });
  });
  const sep = " • ";
  const line = items.map((it) => `${it.icon} ${it.text}`).join(sep);
  return (
    <div className="relative z-20 bg-gradient-to-r from-emerald-700 via-emerald-800 to-emerald-700 text-emerald-50 text-sm shadow-md border-t border-b border-emerald-900/40" data-testid="landing-running-text">
      <div className="max-w-7xl mx-auto flex items-center gap-3 px-4 lg:px-8 h-9 overflow-hidden">
        <span className="shrink-0 flex items-center gap-1.5 bg-amber-400 text-amber-950 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider">
          <Radio className="h-3 w-3 animate-pulse" />Live
        </span>
        <div className="flex-1 overflow-hidden">
          <div className="flex whitespace-nowrap will-change-transform" style={{ animation: "dmr-marquee 55s linear infinite" }}>
            <span className="pr-16">{line}</span>
            <span className="pr-16" aria-hidden="true">{line}</span>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes dmr-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      `}</style>
    </div>
  );
};
